import { Controller } from "@hotwired/stimulus"

// Reusable client-side form validation, composed with `window.ltFormValidators`
// (app/assets/javascripts/lt_form_validators.js) for the rule checks and message show/hide.
//
// Contract for consuming templates:
// - `data-validation-rule` on each `field` target picks the check - "required", "email", "zip",
//   "phone" or "phone_pattern"; extend `isFieldValid` for more.
// - `field` and `message` targets pair by DOM order: the Nth field owns the Nth message.
// - a `disabled` field is skipped entirely, which is how a conditionally-required field (a tax ID
//   hidden outside the countries that need it) opts out of gating submit.
// - the `submit` target, when present, is `disabled` while any field is invalid.
//
// Only `blur` marks a field touched, and only a touched field shows its error; once touched,
// `input` re-validates on every keystroke.
export default class extends Controller {
  static get targets() { return ["field", "message", "submit"]; }

  connect() {
    this.touchedFields = new WeakSet();
    this.messageTargets.forEach((message) => window.ltFormValidators.hideValidationErrorMessage($(message)));
    this.updateSubmit();
  }

  touch(event) {
    this.touchedFields.add(event.currentTarget);
    this.check(event);
  }

  check(event) {
    const field = event.currentTarget;
    const valid = this.isFieldValid(field);
    const shouldShowError = !valid && this.touchedFields.has(field);
    const formGroup = field.closest(".form-group");
    const message = this.messageTargets[this.fieldTargets.indexOf(field)];

    if (formGroup) { formGroup.classList.toggle("has-error", shouldShowError); }

    if (message) {
      if (shouldShowError) {
        window.ltFormValidators.showValidationErrorMessage($(message));
      } else {
        window.ltFormValidators.hideValidationErrorMessage($(message));
      }
    }

    this.updateSubmit();
  }

  isFieldValid(field) {
    const value = field.value;

    switch (field.dataset.validationRule) {
      case "email":
        return !window.ltFormValidators.isBlank(value) && window.ltFormValidators.isValidEmail(value);
      case "zip":
        return !window.ltFormValidators.isBlank(value) && /^(\d{5}(-\d{4})?|[A-Z]\d[A-Z] *\d[A-Z]\d)$/.test(value);
      // The two phone rules are deliberately distinct, or one form silently gets the other's
      // validation. "phone" checks via window.libphonenumber, optionally hinted by
      // `data-validation-country`, and defers a blank to the field's own `required` attribute.
      // "phone_pattern" is a looser regexp check where blank is always valid.
      case "phone":
        return this.isValidPhone(field, value);
      case "phone_pattern":
        return window.ltFormValidators.isBlank(value) || window.ltFormValidators.isValidPhone(value);
      case "required":
      default:
        return !window.ltFormValidators.isBlank(value);
    }
  }

  isValidPhone(field, value) {
    if (window.ltFormValidators.isBlank(value)) { return !field.required; }

    try {
      if (field.dataset.validationCountry) {
        return window.libphonenumber.isValidPhoneNumber(value, field.dataset.validationCountry);
      }
      return window.libphonenumber.isValidPhoneNumber(value);
    } catch (e) {
      return false;
    }
  }

  updateSubmit() {
    if (!this.hasSubmitTarget) { return; }
    this.submitTarget.disabled = !this.fieldTargets
      .filter((field) => !field.disabled)
      .every((field) => this.isFieldValid(field));
  }
}
